import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from '../components/ui';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const MyTenders = () => {
  const { user } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Tender management state
  const [myTenders, setMyTenders] = useState([]);
  const [tenderStats, setTenderStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tenderFilters, setTenderFilters] = useState({
    status_filter: '',
    search: ''
  });
  
  // Proposal inbox state  
  const [proposalsInbox, setProposalsInbox] = useState([]);
  const [inboxFilters, setInboxFilters] = useState({
    status_filter: '',
    tender_filter: ''
  });
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [showProposalDetails, setShowProposalDetails] = useState(false);

  useEffect(() => {
    if (activeTab === 'dashboard' || activeTab === 'tenders') {
      fetchMyTenders();
      fetchTenderStats();
    }
    if (activeTab === 'proposals') {
      fetchProposalsInbox();
    }
  }, [activeTab, tenderFilters, inboxFilters]);

  const fetchMyTenders = async () => {
    try {
      setLoading(true);
      const params = {};
      if (tenderFilters.status_filter) params.status_filter = tenderFilters.status_filter;
      
      const response = await axios.get(`${backendUrl}/api/users/me/tenders`, { 
        params,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setMyTenders(response.data.tenders || []);
    } catch (error) {
      console.error('Error fetching my tenders:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTenderStats = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/users/me/tenders/stats`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setTenderStats(response.data);
    } catch (error) {
      console.error('Error fetching tender stats:', error);
    }
  };

  const fetchProposalsInbox = async () => {
    try {
      setLoading(true);
      const params = {};
      if (inboxFilters.status_filter) params.status_filter = inboxFilters.status_filter;
      if (inboxFilters.tender_filter) params.tender_filter = inboxFilters.tender_filter;
      
      const response = await axios.get(`${backendUrl}/api/users/me/proposals/inbox`, { 
        params,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setProposalsInbox(response.data.proposals || []);
    } catch (error) {
      console.error('Error fetching proposals inbox:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateTenderStatus = async (tenderId, newStatus) => {
    try {
      await axios.put(`${backendUrl}/api/tenders/${tenderId}/status`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      alert(`✅ Tender ${newStatus === 'active' ? 'opened' : 'closed'} successfully!`);
      fetchMyTenders();
    } catch (error) {
      console.error('Error updating tender status:', error);
      alert('❌ Failed to update tender status');
    }
  };

  const deleteTender = async (tenderId) => {
    if (!window.confirm('Are you sure you want to delete this tender? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${backendUrl}/api/tenders/${tenderId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      alert('✅ Tender deleted successfully!');
      fetchMyTenders();
    } catch (error) {
      console.error('Error deleting tender:', error);
      alert('❌ Failed to delete tender');
    }
  };

  const updateProposalStatus = async (tenderId, proposalId, newStatus, feedback = '') => {
    try {
      await axios.put(`${backendUrl}/api/tenders/${tenderId}/proposals/${proposalId}/status`, {
        status: newStatus,
        feedback: feedback,
        close_tender: newStatus === 'accepted' && window.confirm('Would you like to close this tender after accepting this proposal?')
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      alert(`✅ Proposal ${newStatus} successfully!`);
      fetchProposalsInbox();
      setShowProposalDetails(false);
    } catch (error) {
      console.error('Error updating proposal status:', error);
      alert('❌ Failed to update proposal status');
    }
  };

  const openProposalDetails = (proposal) => {
    setSelectedProposal(proposal);
    setShowProposalDetails(true);
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      active: 'bg-green-100 text-green-800',
      closed: 'bg-red-100 text-red-800', 
      draft: 'bg-yellow-100 text-yellow-800',
      submitted: 'bg-blue-100 text-blue-800',
      under_review: 'bg-orange-100 text-orange-800',
      accepted: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      needs_clarification: 'bg-purple-100 text-purple-800'
    };
    
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
      </span>
    );
  };

  if (loading && activeTab === 'dashboard') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Tender Management</h1>
          <p className="mt-2 text-gray-600">Manage your tenders and review proposals</p>
        </div>

        {/* Navigation Tabs */}
        <div className="mb-8">
          <nav className="flex space-x-8">
            {[
              { id: 'dashboard', name: 'Dashboard', icon: '📊' },
              { id: 'tenders', name: 'My Tenders', icon: '📋' },
              { id: 'proposals', name: 'Proposal Inbox', icon: '📥' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-2 px-4 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.name}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && tenderStats && (
          <div className="space-y-8">
            {/* Stats Overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                      <span className="text-white text-sm">📋</span>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Total Tenders</dt>
                      <dd className="text-lg font-medium text-gray-900">{tenderStats.tender_overview.total_tenders}</dd>
                    </dl>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                      <span className="text-white text-sm">🟢</span>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Active Tenders</dt>
                      <dd className="text-lg font-medium text-gray-900">{tenderStats.tender_overview.active_tenders}</dd>
                    </dl>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-orange-500 rounded-md flex items-center justify-center">
                      <span className="text-white text-sm">📥</span>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Total Proposals</dt>
                      <dd className="text-lg font-medium text-gray-900">{tenderStats.proposal_overview.total_proposals}</dd>
                    </dl>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-purple-500 rounded-md flex items-center justify-center">
                      <span className="text-white text-sm">🆕</span>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">New Proposals</dt>
                      <dd className="text-lg font-medium text-gray-900">{tenderStats.proposal_overview.new_proposals}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
              <div className="flex flex-wrap gap-4">
                <Button 
                  onClick={() => window.location.href = '/tenders'}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  🆕 Create New Tender
                </Button>
                <Button 
                  onClick={() => setActiveTab('proposals')}
                  variant="outline"
                >
                  📥 View Proposal Inbox ({tenderStats.proposal_overview.new_proposals} new)
                </Button>
                <Button 
                  onClick={() => setActiveTab('tenders')}
                  variant="outline"
                >
                  📋 Manage My Tenders
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* My Tenders Tab */}
        {activeTab === 'tenders' && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex flex-wrap gap-4">
                <select
                  value={tenderFilters.status_filter}
                  onChange={(e) => setTenderFilters(prev => ({ ...prev, status_filter: e.target.value }))}
                  className="border border-gray-300 rounded-md px-3 py-2"
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="closed">Closed</option>
                  <option value="draft">Draft</option>
                </select>
                
                <Button onClick={() => window.location.href = '/tenders'} className="bg-blue-600 hover:bg-blue-700">
                  🆕 Create New Tender
                </Button>
              </div>
            </div>

            {/* Tenders List */}
            <div className="space-y-4">
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                </div>
              ) : myTenders.length === 0 ? (
                <div className="bg-white rounded-lg shadow p-8 text-center">
                  <p className="text-gray-500">No tenders found. Create your first tender to get started!</p>
                  <Button 
                    onClick={() => window.location.href = '/tenders'}
                    className="mt-4 bg-blue-600 hover:bg-blue-700"
                  >
                    Create First Tender
                  </Button>
                </div>
              ) : (
                myTenders.map((tender) => (
                  <div key={tender.id} className="bg-white rounded-lg shadow p-6">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">{tender.title}</h3>
                          {getStatusBadge(tender.status)}
                        </div>
                        
                        <p className="text-gray-600 mb-4 line-clamp-2">{tender.description}</p>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-500">
                          <div>
                            <span className="font-medium">Proposals:</span> {tender.total_proposals || 0}
                          </div>
                          <div>
                            <span className="font-medium">Budget:</span> ${tender.budget_min} - ${tender.budget_max}
                          </div>
                          <div>
                            <span className="font-medium">Deadline:</span> {new Date(tender.deadline).toLocaleDateString()}
                          </div>
                          <div>
                            <span className="font-medium">Created:</span> {new Date(tender.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Tender Actions */}
                    <div className="mt-6 flex flex-wrap gap-2">
                      <Button 
                        onClick={() => window.location.href = `/tenders`}
                        variant="outline"
                        size="sm"
                      >
                        👁️ View Details
                      </Button>
                      
                      {tender.total_proposals > 0 && (
                        <Button 
                          onClick={() => {
                            setInboxFilters(prev => ({ ...prev, tender_filter: tender.id }));
                            setActiveTab('proposals');
                          }}
                          variant="outline"
                          size="sm"
                        >
                          📥 View Proposals ({tender.total_proposals})
                        </Button>
                      )}
                      
                      {tender.status === 'active' ? (
                        <Button 
                          onClick={() => updateTenderStatus(tender.id, 'closed')}
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-800"
                        >
                          🔒 Close Tender
                        </Button>
                      ) : (
                        <Button 
                          onClick={() => updateTenderStatus(tender.id, 'active')}
                          variant="outline"
                          size="sm"
                          className="text-green-600 hover:text-green-800"
                        >
                          🔓 Reopen Tender
                        </Button>
                      )}
                      
                      <Button 
                        onClick={() => deleteTender(tender.id)}
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-800"
                      >
                        🗑️ Delete
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Proposals Inbox Tab */}
        {activeTab === 'proposals' && (
          <div className="space-y-6">
            {/* Inbox Filters */}
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex flex-wrap gap-4">
                <select
                  value={inboxFilters.status_filter}
                  onChange={(e) => setInboxFilters(prev => ({ ...prev, status_filter: e.target.value }))}
                  className="border border-gray-300 rounded-md px-3 py-2"
                >
                  <option value="">All Status</option>
                  <option value="submitted">New Proposals</option>
                  <option value="under_review">Under Review</option>
                  <option value="accepted">Accepted</option>
                  <option value="rejected">Rejected</option>
                </select>
                
                <select
                  value={inboxFilters.tender_filter}
                  onChange={(e) => setInboxFilters(prev => ({ ...prev, tender_filter: e.target.value }))}
                  className="border border-gray-300 rounded-md px-3 py-2"
                >
                  <option value="">All Tenders</option>
                  {myTenders.map((tender) => (
                    <option key={tender.id} value={tender.id}>{tender.title}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Proposals List */}
            <div className="space-y-4">
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                </div>
              ) : proposalsInbox.length === 0 ? (
                <div className="bg-white rounded-lg shadow p-8 text-center">
                  <p className="text-gray-500">No proposals found.</p>
                </div>
              ) : (
                proposalsInbox.map((proposal) => (
                  <div key={proposal.id} className="bg-white rounded-lg shadow p-6 hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h4 className="font-semibold text-gray-900">{proposal.company_name}</h4>
                          {getStatusBadge(proposal.status)}
                        </div>
                        
                        <p className="text-sm text-gray-600 mb-2">
                          <span className="font-medium">For:</span> {proposal.tender_title}
                        </p>
                        
                        <p className="text-gray-700 mb-3 line-clamp-2">{proposal.proposal_description}</p>
                        
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm text-gray-500">
                          <div>
                            <span className="font-medium">Amount:</span> ${proposal.proposal_amount?.toLocaleString()}
                          </div>
                          <div>
                            <span className="font-medium">Contact:</span> {proposal.contact_person}
                          </div>
                          <div>
                            <span className="font-medium">Submitted:</span> {new Date(proposal.submitted_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Proposal Actions */}
                    <div className="mt-4 flex flex-wrap gap-2">
                      <Button 
                        onClick={() => openProposalDetails(proposal)}
                        size="sm"
                        variant="outline"
                      >
                        👁️ View Details
                      </Button>
                      
                      {proposal.status === 'submitted' && (
                        <>
                          <Button 
                            onClick={() => updateProposalStatus(proposal.tender_id, proposal.id, 'accepted')}
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            ✅ Accept
                          </Button>
                          <Button 
                            onClick={() => updateProposalStatus(proposal.tender_id, proposal.id, 'rejected')}
                            size="sm"
                            className="bg-red-600 hover:bg-red-700 text-white"
                          >
                            ❌ Reject
                          </Button>
                          <Button 
                            onClick={() => updateProposalStatus(proposal.tender_id, proposal.id, 'under_review')}
                            size="sm"
                            variant="outline"
                          >
                            📝 Mark Under Review
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Proposal Details Modal */}
        {showProposalDetails && selectedProposal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-3xl w-full max-h-screen overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">{selectedProposal.company_name}</h2>
                    <p className="text-gray-600">{selectedProposal.tender_title}</p>
                  </div>
                  <button
                    onClick={() => setShowProposalDetails(false)}
                    className="text-gray-400 hover:text-gray-600 text-xl"
                  >
                    ✕
                  </button>
                </div>

                <div className="space-y-6">
                  {/* Proposal Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Contact Person</label>
                      <p className="text-gray-900">{selectedProposal.contact_person}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Proposal Amount</label>
                      <p className="text-gray-900 text-lg font-semibold">${selectedProposal.proposal_amount?.toLocaleString()}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Status</label>
                      {getStatusBadge(selectedProposal.status)}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Submitted</label>
                      <p className="text-gray-900">{new Date(selectedProposal.submitted_at).toLocaleString()}</p>
                    </div>
                  </div>

                  {/* Proposal Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Proposal Description</label>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-gray-900 whitespace-pre-wrap">{selectedProposal.proposal_description}</p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap gap-3 pt-4 border-t">
                    {selectedProposal.status === 'submitted' && (
                      <>
                        <Button 
                          onClick={() => updateProposalStatus(selectedProposal.tender_id, selectedProposal.id, 'accepted')}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          ✅ Accept Proposal
                        </Button>
                        <Button 
                          onClick={() => updateProposalStatus(selectedProposal.tender_id, selectedProposal.id, 'rejected')}
                          className="bg-red-600 hover:bg-red-700"
                        >
                          ❌ Reject Proposal
                        </Button>
                        <Button 
                          onClick={() => updateProposalStatus(selectedProposal.tender_id, selectedProposal.id, 'under_review')}
                          variant="outline"
                        >
                          📝 Mark Under Review
                        </Button>
                      </>
                    )}
                    
                    <Button 
                      onClick={() => setShowProposalDetails(false)}
                      variant="outline"
                    >
                      Close
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyTenders;